import React, { useState } from "react";
import { FiClock, FiDroplet, FiCloudRain, FiToggleLeft, FiToggleRight, FiPlus } from "react-icons/fi";

const TYPE_META = {
  time:     { icon: FiClock,    color: "#667eea", label: "Time-based"     },
  moisture: { icon: FiDroplet,  color: "#10b981", label: "Moisture-based" },
  weather:  { icon: FiCloudRain,color: "#3b82f6", label: "Weather-based"  },
};

const IrrSchedulingPanel = ({ d }) => {
  const [schedules, setSchedules] = useState(d.schedule);
  const [filter, setFilter] = useState("All");

  const filters = ["All", "time", "moisture", "weather"];
  const visible = filter === "All" ? schedules : schedules.filter(s => s.type === filter);

  const toggleEnabled = (id) => {
    setSchedules(prev => prev.map(s => s.id === id ? { ...s, enabled: !s.enabled } : s));
  };

  return (
    <div className="irr-sched-panel">

      {/* Header */}
      <div className="irr-sched-header">
        <div>
          <div className="irr-section-title">Irrigation Scheduling</div>
          <div className="irr-section-sub">Configure time-based, moisture-triggered and weather-aware schedules</div>
        </div>
        <button className="irr-ctrl-btn irr-ctrl-purple" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <FiPlus size={14} /> Add Schedule
        </button>
      </div>

      {/* Type legend */}
      <div className="irr-sched-legend">
        {Object.entries(TYPE_META).map(([k, m]) => (
          <div key={k} className="irr-sched-leg-item" style={{ borderColor: m.color }}>
            <m.icon size={14} color={m.color} /><span style={{ color: m.color }}>{m.label}</span>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="irr-map-filter" style={{ marginBottom: 12 }}>
        {filters.map(f => (
          <button key={f} className={`irr-filter-chip ${filter === f ? "irr-filter-active" : ""}`} onClick={() => setFilter(f)}>
            {f === "All" ? "All Types" : TYPE_META[f].label}
          </button>
        ))}
      </div>

      {/* Schedule cards */}
      <div className="irr-sched-list">
        {visible.map(sch => {
          const meta = TYPE_META[sch.type];
          return (
            <div key={sch.id} className={`irr-sched-card ${!sch.enabled ? "irr-sched-disabled" : ""}`} style={{ borderLeftColor: meta.color }}>
              <div className="irr-sc-top">
                <div className="irr-sc-type-chip" style={{ background: meta.color + "18", color: meta.color }}>
                  <meta.icon size={12} /> {meta.label}
                </div>
                <div className="irr-sc-id">{sch.id}</div>
                <button className="irr-toggle-btn" onClick={() => toggleEnabled(sch.id)} title={sch.enabled ? "Disable" : "Enable"}>
                  {sch.enabled
                    ? <FiToggleRight size={22} color="#10b981" />
                    : <FiToggleLeft  size={22} color="#9ca3af" />}
                </button>
              </div>

              <div className="irr-sc-zone">{sch.zone}</div>

              {/* Type-specific details */}
              {sch.type === "time" && (
                <div className="irr-sc-details">
                  <span>⏰ {sch.startTime}</span>
                  <span>⏱ {sch.duration} min</span>
                  <span>📅 {Array.isArray(sch.days) ? sch.days.join(", ") : sch.days}</span>
                </div>
              )}
              {sch.type === "moisture" && (
                <div className="irr-sc-details">
                  <span>▶ Start when moisture &lt; {sch.startTrigger}%</span>
                  <span>⏹ Stop when moisture &gt; {sch.stopTrigger}%</span>
                  <span>⏱ Max {sch.maxDuration} min</span>
                </div>
              )}
              {sch.type === "weather" && (
                <div className="irr-sc-details">
                  <span>🌧 Skip if rain &gt; {sch.rainThreshold}mm forecast</span>
                  <span>Auto-evaluates daily weather data</span>
                </div>
              )}

              <div className="irr-sc-next">
                <span className={`irr-badge ${sch.enabled ? "" : "irr-badge-gray"}`}
                  style={sch.enabled ? { background: meta.color + "18", color: meta.color } : { background: "#f3f4f6", color: "#9ca3af" }}>
                  {sch.enabled ? `Next: ${sch.nextRun}` : "Disabled"}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Safety rules note */}
      <div className="irr-safety-note">
        <div className="irr-safety-title">⚙ Safety Thresholds (Always Active)</div>
        <div className="irr-safety-list">
          <div>• Pressure safety stop: &lt; 1.5 bar → auto-close valve</div>
          <div>• Dry-run protection: No flow &gt; 90s after valve open → stop pump</div>
          <div>• Flow cutoff: If flow exceeds 500 L/min (pipe burst) → emergency close</div>
          <div>• Max irrigation duration: 3 hours per zone per day</div>
        </div>
      </div>
    </div>
  );
};

export default IrrSchedulingPanel;
